<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import Steps from "@/components/Steps.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Abschluss from "@/components/abschluss/Abschluss.vue";
import ProduktmoduleVGV from "@/components/module/produktmoduleVGV";
import { NavvItem } from "@/model/NavItem";
import GebaeudedatenUbersicht from "@/components/abschluss/GebaeudeUebersicht.vue";
import Logger from "js-logger";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zeile from "@/components/grid/Zeile.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussVGV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Abschluss,
    Steps,
    GebaeudedatenUbersicht,
    Zelle,
    TitelZelle,
    Zeile,
  },
  props: {
    isMakler: Boolean,
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props) {
    const piniaStore = useFormStore();
    const produktmodule = ref(ProduktmoduleVGV);

    const fehler = ref({} as any);

    const { formatCurrency } = formatUtils();

    const hatZusatzmodule = computed(() => {
      for (const modul of produktmodule.value) {
        if (modul.modulId in piniaStore.state.tarifinformationen) {
          return true;
        }
      }
      return false;
    });

    const iddText = computed(() => {
      const gewerbe = piniaStore.state.gebaeudedaten.gewerbeVorhanden === true;
      const denkmalschutz = piniaStore.state.gebaeudedaten.denkmalschutz;
      Logger.debug("IDD Text", gewerbe, denkmalschutz);

      if (gewerbe) {
        // Gewerbe und Denkmalschutz
        if (denkmalschutz) {
          return "Ich bestätige, dass kein Schwimmbad im Haus vorhanden ist.";
        }
        // Gewerbe und kein Denkmalschutz
        else {
          return "Ich bestätige, dass mein Haus nicht unter Denkmalschutz steht und dass kein Schwimmbad im Haus vorhanden ist.";
        }
      } else {
        // Kein Gewerbe und Denkmalschutz
        if (denkmalschutz) {
          return "Ich bestätige, dass kein Gewerbebetrieb und kein Schwimmbad im Haus vorhanden sind.";
        }
        // Kein Gewerbe und kein Denkmalschutz
        else {
          return "Ich bestätige, dass mein Haus nicht unter Denkmalschutz steht und dass kein Gewerbebetrieb und kein Schwimmbad im Haus vorhanden sind.";
        }
      }
    });

    const zusatzModule = computed(() => {
      let text = "";

      for (const modul of produktmodule.value) {
        if ((piniaStore.state.tarifinformationen as any)[modul.modulId]) {
          if (text.length > 0) {
            text += ", ";
          }
          text += modul.name;
        }
      }

      return text;
    });

    const zusaetzlicheSbLw = computed(() => {
      return piniaStore.state.tarifinformationen.extraSelbstbeteiligungLeitungswasser > 0;
    });

    return {
      props,
      piniaStore,
      fehler,
      formatCurrency,
      hatZusatzmodule,
      zusatzModule,
      zusaetzlicheSbLw,
      iddText,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="4" @goto-page="$emit('goto-page', $event)" />

  <abschluss
    produkt="vgv"
    :zeige-versichertes-objekt="true"
    :zusaetzliche-SbLw="zusaetzlicheSbLw"
    :zeige-tarif="true"
    @next-page="$emit('next-page', $event)"
    @goto-page="$emit('goto-page', $event)"
  >
    <template v-slot:versichertes-objekt>
      <gebaeudedaten-ubersicht />
    </template>

    <template v-slot:zusatzmodule v-if="hatZusatzmodule"><br />{{ zusatzModule }}</template>

    <template v-if="piniaStore.state.tarifinformationen.extraSelbstbeteiligung" v-slot:zusaetzliche-selbstbeteiligung>
      <zeile class="u-mar-t-2">
        <titel-zelle :breite="3" class="o-grid__cell--4" :ausrichtung-responsiv="false" :vertikal-zentrieren="false">
          Zu&shy;sätz&shy;liche Selbst&shy;be&shy;teili&shy;gung je Grund&shy;ge&shy;fahr-Schaden&nbsp;
        </titel-zelle>
        <zelle :breite="9" class="o-grid__cell--8" data-cy="zusaetzlicheSb">
          <strong>{{ formatCurrency(piniaStore.state.tarifinformationen.extraSelbstbeteiligung) }}</strong>
        </zelle>
      </zeile>
    </template>

    <template v-slot:angaben-bestaetigen>
      Der Antrag wurde gemäß meiner Angaben erstellt. {{ iddText }} Auf eine Beratung habe ich verzichtet und damit auf
      den Schadenersatzanspruch nach §6, Abs. 5 VVG.
    </template>
  </abschluss>
</template>
